import { registerHandle, resolve, summarizeRef, SourceSchema, type Source } from '@/agent/refs';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldList,
  translateAttributes,
} from './shared/layers';
import type { Tool } from './types';

type Input = {
  layer_id: string;
  where: string;
  order_by?: string;
  order_dir?: 'ASC' | 'DESC';
  limit?: number;
  in_current_extent?: boolean;
  spatial_filter?: Source;
  sample_fields?: string[];
};

export const resolveFeatures: Tool<Input> = {
  name: 'resolve_features',
  description:
    'Run a feature_set query and stash the result behind a handle. Returns a small attribute sample, the matched objectids, the count, and a centroid/extent summary. spatial_filter (any Source — typically a buffer handle) restricts results to features intersecting that geometry, so render(handle) draws only the matching subset. No map side-effects.',
  input_schema: {
    type: 'object',
    properties: {
      layer_id: { type: 'string' },
      where: { type: 'string', description: 'SQL WHERE clause; use "1=1" for the whole layer.' },
      order_by: { type: 'string' },
      order_dir: { enum: ['ASC', 'DESC'] },
      limit: { type: 'integer', minimum: 1, maximum: 5000 },
      in_current_extent: { type: 'boolean' },
      spatial_filter: SourceSchema,
      sample_fields: {
        type: 'array',
        items: { type: 'string' },
        description: 'Optional list of fields to include in the small attribute sample.',
      },
    },
    required: ['layer_id', 'where'],
    additionalProperties: false,
  },
  execute: async (input) => {
    const ref = await resolve({
      kind: 'feature_set',
      layer_id: input.layer_id,
      where: input.where,
      order_by: input.order_by,
      order_dir: input.order_dir,
      limit: input.limit,
      in_current_extent: input.in_current_extent,
      spatial_filter: input.spatial_filter,
    });
    const handle = registerHandle(ref);

    const layer = await ensureFeatureLayer(input.layer_id);
    const oidField = layer.objectIdField || 'objectid';
    const probe = requireLayerRecord(input.layer_id).probe;

    let sample: Array<Record<string, unknown>> = [];
    const sampleOids = ref.feature_set?.objectids.slice(0, 5) ?? [];
    if (sampleOids.length > 0) {
      const rs = await layer.queryFeatures({
        where: `${oidField} IN (${sampleOids.join(',')})`,
        outFields: resolveFieldList(input.sample_fields, probe, layer) ?? ['*'],
        returnGeometry: false,
        num: sampleOids.length,
      });
      sample = rs.features.map((f) => translateAttributes(f.attributes ?? {}, probe));
    }

    return {
      handle,
      summary: summarizeRef(ref),
      objectids: ref.feature_set?.objectids ?? [],
      sample,
    };
  },
};
